'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowRight, PackageSearch, ShoppingCart, Smartphone } from 'lucide-react';
import { BottomNav } from '@/components/mobile/bottom-nav';
import { NativeProductCard } from '@/components/mobile/native-product-card';
import { useDeviceDetail } from '@/hooks/useDeviceDetail';
import { useCart } from '@/hooks/useCart';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface MobileDeviceDetailViewProps {
  slug: string;
}

/**
 * Native-app device result (`/products/device/[slug]` below `md`):
 * phone hero header (photo or fallback, names, brand badge,
 * compatible count), live category chips filtering the loaded
 * compatible grid (reused native product card verbatim), and friendly
 * empty states linking back to the browser and the full catalog.
 * Desktop tree is untouched.
 */
export function MobileDeviceDetailView({ slug }: MobileDeviceDetailViewProps) {
  const {
    device,
    brand,
    categories,
    compatibleCount,
    visibleProducts,
    selectedCategory,
    setSelectedCategory,
    loading,
    notFound,
  } = useDeviceDetail(slug);
  const { itemCount } = useCart();
  // Same persisted-cart hydration guard as BottomNav: server renders 0.
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  return (
    <div dir="rtl" className="min-h-screen bg-background pb-24 md:hidden">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-background/95 px-4 py-3 backdrop-blur">
        <Link
          href="/devices"
          aria-label="رجوع إلى الموديلات"
          className="flex h-11 w-11 items-center justify-center rounded-full text-foreground"
        >
          <ArrowRight className="h-6 w-6" />
        </Link>
        <h1 className="line-clamp-1 flex-1 px-2 text-center text-base font-extrabold">
          {device?.nameAr ?? 'المنتجات المتوافقة'}
        </h1>
        <Link
          href="/order"
          aria-label="السلة"
          className="relative flex h-11 w-11 items-center justify-center rounded-full text-foreground"
        >
          <ShoppingCart className="h-6 w-6" />
          {mounted && itemCount > 0 && (
            <span className="absolute -top-0.5 -start-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
              {itemCount}
            </span>
          )}
        </Link>
      </header>

      <main className="px-4">
        {loading ? (
          <div className="mt-4 space-y-3" aria-label="جارٍ تحميل المنتجات المتوافقة">
            <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-4">
              <div className="h-20 w-20 shrink-0 animate-pulse rounded-2xl bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
                <div className="h-3 w-1/3 animate-pulse rounded bg-muted" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-56 animate-pulse rounded-2xl bg-muted" />
              ))}
            </div>
          </div>
        ) : notFound || !device ? (
          <div className="mt-4 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-12 text-center">
            <PackageSearch className="h-12 w-12 text-muted-foreground/50" />
            <p className="text-sm font-bold">الموديل غير موجود</p>
            <p className="max-w-sm text-xs text-muted-foreground">
              تحقق من الرابط أو تصفح موديلاً آخر لعرض المنتجات المتوافقة معه.
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-3">
              <Button variant="outline" className="min-h-11 rounded-full px-6" render={<Link href="/devices" />}>
                تصفح الموديلات
              </Button>
              <Button className="min-h-11 rounded-full px-6" render={<Link href="/products" />}>
                جميع المنتجات
              </Button>
            </div>
          </div>
        ) : (
          <>
            <section className="mt-4 flex items-center gap-4 rounded-2xl border border-border bg-card p-4 text-start">
              <span className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-muted">
                {device.imageUrl ? (
                  <Image
                    src={device.imageUrl}
                    alt={device.nameAr}
                    width={80}
                    height={80}
                    className="h-full w-full object-contain p-2"
                  />
                ) : (
                  <span className="flex flex-col items-center gap-1 text-muted-foreground">
                    <Smartphone className="h-7 w-7" />
                    <span className="text-xl font-black">{device.nameAr.charAt(0)}</span>
                  </span>
                )}
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  {brand ? (
                    <Badge variant="secondary" className="px-3 py-1 text-xs font-bold">
                      {brand.nameAr}
                    </Badge>
                  ) : null}
                  <span className="text-xs font-semibold text-muted-foreground">
                    {compatibleCount} منتجات متوافقة
                  </span>
                </div>
                <h2 className="mt-1.5 line-clamp-2 text-lg font-black" dir="auto">
                  {device.nameAr}
                </h2>
                {device.nameEn ? (
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground" dir="ltr">
                    {device.nameEn}
                  </p>
                ) : null}
              </div>
            </section>

            {compatibleCount === 0 ? (
              <div className="mt-3 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-12 text-center">
                <PackageSearch className="h-12 w-12 text-muted-foreground/50" />
                <p className="text-sm font-bold">لا توجد منتجات متوافقة مع هذا الموديل بعد</p>
                <p className="max-w-sm text-xs text-muted-foreground">
                  تصفح موديلاً آخر أو اكتشف جميع المنتجات في المتجر.
                </p>
                <div className="mt-2 flex flex-wrap justify-center gap-3">
                  <Button variant="outline" className="min-h-11 rounded-full px-6" render={<Link href="/devices" />}>
                    تصفح الموديلات
                  </Button>
                  <Button className="min-h-11 rounded-full px-6" render={<Link href="/products" />}>
                    جميع المنتجات
                  </Button>
                </div>
              </div>
            ) : (
              <>
                {categories.length > 0 ? (
                  <div
                    className="-mx-4 mt-4 flex gap-2 overflow-x-auto px-4 pb-1"
                    role="group"
                    aria-label="تصفية حسب القسم"
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedCategory(null)}
                      aria-pressed={selectedCategory === null}
                      className={cn(
                        'flex min-h-11 shrink-0 items-center rounded-full border px-5 text-sm font-bold transition-colors',
                        selectedCategory === null
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-card text-foreground active:bg-muted'
                      )}
                    >
                      الكل
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category.slug}
                        type="button"
                        onClick={() =>
                          setSelectedCategory(
                            selectedCategory === category.slug ? null : category.slug
                          )
                        }
                        aria-pressed={selectedCategory === category.slug}
                        className={cn(
                          'flex min-h-11 shrink-0 items-center rounded-full border px-5 text-sm font-bold transition-colors',
                          selectedCategory === category.slug
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-card text-foreground active:bg-muted'
                        )}
                      >
                        {category.nameAr}
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="mt-3 flex items-center justify-between">
                  <h3 className="text-sm font-extrabold">
                    {selectedCategory
                      ? categories.find((category) => category.slug === selectedCategory)?.nameAr ??
                        'منتجات مصفاة'
                      : 'كل المنتجات المتوافقة'}
                  </h3>
                  <p className="text-xs font-bold text-muted-foreground">
                    {visibleProducts.length} منتجات
                  </p>
                </div>

                {visibleProducts.length === 0 ? (
                  <div className="mt-3 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border bg-card px-6 py-12 text-center">
                    <PackageSearch className="h-12 w-12 text-muted-foreground/50" />
                    <p className="text-sm font-bold">لا توجد منتجات في هذا القسم</p>
                    <p className="max-w-sm text-xs text-muted-foreground">
                      جرّب قسماً آخر أو اعرض كل المنتجات المتوافقة مع هذا الموديل.
                    </p>
                    <Button
                      type="button"
                      onClick={() => setSelectedCategory(null)}
                      variant="outline"
                      className="mt-2 min-h-11 rounded-full px-6"
                    >
                      عرض الكل
                    </Button>
                  </div>
                ) : (
                  <section className="mt-3 grid grid-cols-2 gap-3">
                    {visibleProducts.map((product) => (
                      <NativeProductCard key={product.id} product={product} />
                    ))}
                  </section>
                )}
              </>
            )}
          </>
        )}
      </main>

      <BottomNav />
    </div>
  );
}
